<script>
import Vue from 'vue'
import moment from 'moment'
import AgGridMethods from '@/Mixins/SigmaClientNew/AgGridMethods'
import AgGridProscanColumns from '@/Mixins/SigmaClientNew/AgGridProscanColumns'
import { AgGridVue } from 'ag-grid-vue'
import { mapGetters } from 'vuex'
import { getHeaders, buildUrlWithQuery } from '@/utils/api'
import AgGridIconRender from '@/components/SigmaClientNew/AgGridIconRender'
import AgGridSearchFilter from '@/components/SigmaClientNew/AgGridSearchFilter'
import AgGridSearchFloatingFilter from '@/components/SigmaClientNew/AgGridSearchFloatingFilter'
import AgGridTextFloatingFilter from '@/components/SigmaClientNew/AgGridTextFloatingFilter'

export default Vue.extend({
  name: 'ProscanList',

  components: {
    AgGridVue
  },
  mixins: [ AgGridMethods, AgGridProscanColumns ],

  props: {
    // Name of input field.  Not really necessary, but helpful if we use the form object down the road
    name: {
      required: true,
      type: String
    },

    iteration: {
      required: false,
      type: Number
    },

    classes: {
      required: false,
      type: String,
      default: 'ag-theme-balham'
    },

    rows: {
      required: true
    },

    // Ag-grid column defs
    columns: {
      required: false,
      type: Array,
      default: undefined
    },

    // Ag-grid Options
    options: {
      required: false,
      type: Object,
      default: undefined
    },
    aggridColumns: {
      required: false,
      type: Array
    },
    loadingRelated: {
      required: false,
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      isError: false, // If the input or selected field is incorrect, show an error
      isLoading: false,
      scope: this,
      gridApi: null,
      columnApi: null,
      defaultColDefs: undefined,
      defaultGridOptions: undefined,
      changes: [],
      rowHeight: 40,
      domLayout: 'autoHeight',
      frameworkComponents: null,
      filterDebounceTimer: null,
      suppressFilterFetch: false,
      lastFilterModel: null,
      /**
       * The query parameters the column filters have set. The store merges what it is handed and
       * keeps the rest, so a parameter is only taken back out by the grid that put it there: these
       * are what a column dropping its criteria - ticking every value, emptying a search box - is
       * cleared from. Parameters the view's own filters set are not in here and are left alone.
       */
      gridQueryParams: [],
      // Parameter each column filters through
      columnQueryParams: {
        transaction_id: 'transaction_id__in',
        document_type: 'document_type__in',
        file_name: 'file_name__icontains',
        vendor: 'vendor__in',
        batch: 'batch__in',
        status: 'status__in',
        info: 'info__icontains',
        created_at: 'created_at'
      }
    }
  },

  computed: {
    ...mapGetters({
      currentApsPage: 'getCurrentApPage',
      currentPaymentsPage: 'getCurrentPaymentPage',
      currentCheckPage: 'getCurrentCheckPage',
      currentPettycashPage: 'getCurrentPcPage',
      currentJesPage: 'getCurrentJePage',
      currentPosPage: 'getCurrentPoPage',
      selection: 'getSelectedTransactions',
      getCurrencyByBatchId: 'getCurrencyByBatchId',
      getFilters: 'getFilters',
      isFilterLoading: 'getTransactionFilterStatus'
    }),
    currentPage () {
      const routeName = {
        'Proscan': 'currentProscanPage'
      }
      return this[routeName[this.$route.name]]
    },
    columnDefs () {
      if (this.columns && Array.isArray(this.columns)) {
        return this.columns
      } else {
        return this.defaultColDefs
      }
    },
    gridOptions () {
      return this.options || this.defaultGridOptions
    },

    // Since we are now sending invoices grouped by invoice, this helper updates each row in the group
    updateChildren () {
      if (!this.rows.length) return false

      this.rows.forEach((group) => {
        group.attributes.resources.forEach((row) => {
          if (!row.data) return false

          row.data.set('payment_confirmation_number', group.attributes.payment_confirmation_number)
          try {
            row.data.save()
          } catch (error) {
            console.log(error)
            return false
          }
        })
      })

      return true
    },
     // Any groups with at least one transaction selected
    selectedNodes () {
      let aggregatedNodes = []
      this.gridApi.forEachNode((node) => {
        console.log('node', node)
        if (node.selected) {
          aggregatedNodes.push(node)
        }
      })
      return aggregatedNodes
    },

    // Since we are now sending invoices grouped by invoice, this helper makes each group look like a transaction
    simulatedPaymentTransactionData () {
      if (!this.selectedNodes.length) return []

      let transactions = []
      this.selectedNodes.forEach((node) => {
        const transaction = {
          attributes: node.data.attributes
        }
        transactions.push(transaction)
      })

      return transactions
    },
    width () {
      return `calc(100vw - 200px)`
    }
  },

  methods: {
    // Ag-grid
    onGridReady (params) {
      if (!params) return
      this.gridApi = params.api
      this.columnApi = params.columnApi
      if (this.isFilterLoading) this.gridApi.showLoadingOverlay()
      const gridElement = document.querySelector('.ag-theme-balham')
      if (gridElement) {
        gridElement.addEventListener('click', this.handleGridClick)
      }
    },
    handleGridClick (event) {
      const target = event.target
      if (target.classList.contains('clickable-link')) {
        const path = target.dataset.path // Extract the path from the dataset
        this.$router.push(path) // Navigate programmatically using Vue Router
        return
      }
      // The file name column, which opens the scanned file rather than navigating anywhere
      if (target.classList.contains('file-download-link')) {
        this.openFile(target.dataset.fileId)
        return
      }
      // The vendor column: the form it opens in belongs to the view this grid sits in
      if (target.classList.contains('vendor-link')) {
        this.$emit('openVendor', target.dataset.vendorId)
      }
    },
    /**
     * Opens a scanned file in a tab of its own. The API hands out the url for it a request at a
     * time, so the tab is opened on the click itself and pointed at the url once it comes back:
     * a tab opened after the round trip is one the browser puts down as a popup.
     * @param fileId   Id of the file behind the row, i.e. file_data.id
     */
    async openFile (fileId) {
      if (!fileId) return
      const tab = window.open('', '_blank')
      try {
        const instanceUrl = this.$store.getters.getActiveInstance.url
        const headers = getHeaders(this.$store.getters.getToken)
        const response = await Vue.http.get(buildUrlWithQuery(`files/${fileId}/download`, {}, instanceUrl), { headers })
        const url = response && response.body && response.body.download_url
        if (!url) throw new Error(`No download url for file ${fileId}`)
        // The tab was opened blank on the click, so it is only sent on its way here
        if (tab) tab.location.href = url
        else window.open(url, '_blank')
      } catch (error) {
        console.log(error)
        if (tab) tab.close()
      }
    },
    onFirstDataRendered (params) {
      if (!params) return
      if (params.api.gridPanel.bodyHeight) params.api.sizeColumnsToFit()
    },
    /**
     * Fetches the rows matching the query. The results go through the store, which is what feeds
     * the 'rows' prop and the record count the pagination is built from.
     * @param query
     * @param filterModel   The criteria the query was built from, put back if the refresh loses them
     */
    async fetchFilteredRows (query, filterModel = null) {
      this.lastFilterModel = JSON.stringify(filterModel || {})
      this.$store.commit('SET_FILTER_STATUS', true)
      try {
        await this.syncStoreFilters(query)
        await this.$store.dispatch('setProscanFilterInfo', Object.assign({}, query))
        // Results are merged into what the store already holds, keyed by their position in the page,
        // so the rows the last query returned are dropped first: a narrower result set replaces them
        // rather than leaving the ones it did not reach behind
        await Promise.all([
          this.$store.dispatch('resetProscanLogMeta'),
          this.$store.dispatch('clearProscanLogState', true),
          this.$store.dispatch('clearSetProscanFilterInfo')
        ])
        this.gridApi.showLoadingOverlay()
        // Filtering starts from the first page again, which the view showing the rows has to know
        this.$emit('filtersApplied', query.page || 1)
        const payload = { query }
        const entities = await this.$store.dispatch('fetchProscanLogsWithQuery', payload)
        if (entities) {
          const promises = await this.$store.dispatch('findDependencies', entities)
          await Promise.all(promises)
        }
      } catch (error) {
        console.log(error)
      } finally {
        this.$store.commit('SET_FILTER_STATUS', false)
        this.restoreFilterModel(filterModel)
      }
    },
    async syncStoreFilters (query) {
      const cleared = this.gridQueryParams.filter(param => !query.hasOwnProperty(param))
      for (const param of cleared) {
        await this.$store.dispatch('unsetFilter', param)
      }
      await this.$store.dispatch('setFilter', Object.assign({}, query))
    },
    onFilterChanged (params) {
      if (!params || !this.gridApi) return
      if (this.suppressFilterFetch) return
      const filterModel = this.activeFilterModel(this.gridApi.getFilterModel())
      if (JSON.stringify(filterModel) === this.lastFilterModel) return
      this.gridApi.showLoadingOverlay()
      if (this.filterDebounceTimer) {
        clearTimeout(this.filterDebounceTimer)
      }
      this.filterDebounceTimer = setTimeout(() => {
        this.fetchFilteredRows(this.buildFilterQuery(filterModel, 1), filterModel)
      }, 250)
    },
    activeFilterModel (filterModel) {
      const active = {}
      Object.keys(filterModel || {}).forEach(columnId => {
        const model = filterModel[columnId]
        if (!model) return
        if (Array.isArray(model.values) && !model.values.length) return
        active[columnId] = model
      })
      return active
    },
    /**
     * Builds the API query for the current column filters
     * @param filterModel   ag-grid filter model
     * @param page          Page to request
     * @return {Object}
     */
    buildFilterQuery (filterModel = {}, page = 1) {
      const columnFilters = {}

      Object.entries(filterModel || {}).forEach(([columnId, model]) => {
        if (!model) return
        const values = Array.isArray(model.values)
          ? model.values.filter(value => value !== null && value !== undefined && String(value).trim())
          : []
        const textValue = typeof model.filter === 'string' ? model.filter.trim() : ''
        const param = this.columnQueryParams[columnId]

        switch (columnId) {
          case 'transaction_id':
            // Several transactions can be asked for at once, separated by spaces or commas
            const transactionIds = textValue.split(/[\s,]+/).filter(Boolean)
            if (transactionIds.length) columnFilters[param] = transactionIds
            break
          case 'document_type':
          case 'vendor':
          case 'batch':
          case 'status':
            if (values.length) columnFilters[param] = values
            break
          case 'file_name':
          case 'info':
            if (textValue) columnFilters[param] = textValue
            break
          case 'created_at':
            Object.assign(columnFilters, this.dateRangeParams(param, model))
            break
          default:
            // A column the API has no parameter of its own for is searched for as free text
            if (textValue) columnFilters.search = textValue
        }
      })

      // What the columns ask for now is theirs to clear next time round
      this.gridQueryParams = [...new Set([...this.gridQueryParams, ...Object.keys(columnFilters)])]

      return Object.assign(this.baseFilterQuery(), { page }, columnFilters)
    },
    /**
     * The filters the view holds, which the columns are queried alongside rather than instead of
     * @return {Object}
     */
    baseFilterQuery () {
      const storeFilters = Object.assign({}, this.getFilters)
      // The page is the one being asked for, and the rest is whatever the columns say
      delete storeFilters.page
      this.gridQueryParams.forEach(param => delete storeFilters[param])
      return storeFilters
    },
    /**
     * The criteria for a timestamp the column shows as a date. The dates on screen are in the
     * browser's timezone and the API keeps its timestamps in UTC, so the day picked is asked for as
     * the span of time that day covers there - the rows that come back are the ones the column shows
     * under that date.
     * @param field   Field the criteria are for, e.g. 'created_at'
     * @param model   ag-grid date filter model, whose dates are read in the browser's timezone
     * @return {Object}
     */
    dateRangeParams (field, model) {
      // ag-grid hands its dates over as 'YYYY-MM-DD HH:mm:ss', of which only the day is asked for
      const asDay = (value) => (typeof value === 'string' && value ? moment(value.split(' ')[0], 'YYYY-MM-DD', true) : null)
      const from = asDay(model.dateFrom)
      if (!(from && from.isValid())) return {}
      const to = asDay(model.dateTo)
      return {
        [`${field}__gte`]: from.clone().startOf('day').toISOString(),
        [`${field}__lte`]: (to && to.isValid() ? to : from).clone().endOf('day').toISOString()
      }
    },
    restoreFilterModel (filterModel) {
      if (!(filterModel && Object.keys(filterModel).length) || !this.gridApi) return
      const current = this.activeFilterModel(this.gridApi.getFilterModel())
      // A column keeps its criteria whichever filter triggered the refresh: what was typed into the
      // text filters is how the user reads back what they searched for
      const dropped = Object.keys(filterModel).some(columnId => !current[columnId])
      if (!dropped) return
      this.suppressFilterFetch = true
      // The model the rows already answer to, so putting it back does not filter again
      this.gridApi.setFilterModel(filterModel)
      this.$nextTick(() => {
        this.suppressFilterFetch = false
      })
    },
    onChangeVisibleColumns () {
      this.$nextTick(() => {
        this.gridApi.sizeColumnsToFit()
      })
    },

    exportToExcel () {
      this.gridApi.exportDataAsExcel()
    },
    updateSelection (gridApi) {
      const api = gridApi || this.gridApi
      if (!api) return
      let selected = []
      api.forEachNode((row) => {
        if (row && row.selected && row.data) {
          selected.push(row.data)
        }
      })
      if (this.emitSelect) {
        this.$emit('onSelect', selected)
      } else {
        let notThisPage = this.selection.filter(v => v.page !== this.currentPage)
        this.$store.dispatch('updateTransactionSelection', [...notThisPage, ...selected])
      }
    },
    prepareColumns (columns) {
      if (columns && columns.length) {
        return this.aggridColumns.map((c) => this[c])
      } else {
        return [
          this.transactionIdColumn,
          this.documentTypeColumn,
          this.fileNameColumn,
          this.vendorColumn,
          this.batchColumn,
          this.statusColumn,
          this.infoColumn,
          this.uploadedAtColumn
        ]
      }
    },
    deselectAll () {
      this.gridOptions.api.deselectAll()
    },
    clickhandleVoidDialogVisible (data) {
      this.$emit('handleVoidDialogVisible', data)
    },
    postPayment (data) {
      this.$emit('postPayment', data)
    },
    getLoading () {
      return this.loadingRelated
    },
    restoreSelection (data, key = 'id', page = this.currentPage) {
      if (!(data && data.length)) return
      const thisPageSelectedIds = data
        .filter(v => v.page === page)
        .map(v => v[key])
      this.gridOptions.api.forEachNode(node => node.setSelected(thisPageSelectedIds.includes(node.data[key])))
    }
  },
  beforeMount () {
    this.defaultColDefs = this.prepareColumns(this.aggridColumns)
    this.defaultGridOptions = {
      enterMovesDown: true,
      enterMovesDownAfterEdit: true,
      defaultColDef: {
        resizable: true,
        suppressSizeToFit: false
      },
      animateRows: true,
      /**
       * Popups are kept inside their parent's bounds, and with an auto height grid that parent is
       * only as tall as the rows it holds. Hanging them off the body keeps the filter lists whole
       * when there is little or no data behind them.
       */
      popupParent: document.body,
      onGridReady: (params) => this.onGridReady(params),
      onFirstDataRendered: (params) => this.onFirstDataRendered(params),
      onSelectionChanged: (params) => {
        this.updateSelection(params.api)
      },
      onFilterChanged: (params) => {
        this.onFilterChanged(params)
      },
      getContextMenuItems: (params) => {
        const defaultItems = ['copy', 'copyWithHeaders', 'separator', 'export']
        if (params.column && (params.column.getColId() === 'transaction_id')) {
          defaultItems.unshift({
            name: 'Open in New Tab',
            action: () => {
              const trnRoute = { 'AP': 'aps', 'PO': 'pos' }
              const row = params.node.data
              const type = row.transaction_type ? trnRoute[row.transaction_type] : ''
              // Carries the log id so the transaction opens with its source document beside it
              const url = type && row.transaction_id
                ? `/${type}/${row.transaction_id}?proscan_log=${row.log_id}`
                : ''
              if (url) {
                window.open(url, '_blank')
              }
            },
            icon: '<span class="ag-icon ag-icon-link"></span>'
          })
        }
        return defaultItems
      }
    }
    this.frameworkComponents = {
      agGridIconRender: AgGridIconRender,
      agGridSearchFilter: AgGridSearchFilter,
      agGridSearchFloatingFilter: AgGridSearchFloatingFilter,
      agGridTextFloatingFilter: AgGridTextFloatingFilter
    }
  },
  watch: {
    aggridColumns (val) {
      this.gridOptions.api.setColumnDefs([])
      this.gridOptions.api.setColumnDefs(this.prepareColumns(val))
    },
    selection (val) {
      if (this.gridApi) {
        const selectedData = this.gridApi.getSelectedRows()
        if (!(val && val.length) && selectedData && selectedData.length) {
          this.gridOptions.api.deselectAll()
        }
      }
    },
    isFilterLoading (value) {
      if (!this.gridApi) return
      this.$nextTick(() => {
        if (value) {
          this.gridApi.showLoadingOverlay()
        } else if (this.rows && this.rows.length) {
          this.gridApi.hideOverlay()
        } else {
          this.gridApi.showNoRowsOverlay()
        }
      })
    }
  }
})
</script>

<template>
  <ag-grid-vue
    :name="name"
    :ref="name"
    :class="classes"
    :rowHeight="rowHeight"
    :style="{width, margin: '17px'}"
    :column-defs="columnDefs"
    :row-data="rows"
    :grid-options="gridOptions"
    :dom-layout="domLayout"
    :frameworkComponents="frameworkComponents"
  />
</template>
<style scoped>
.ag-theme-balham .ag-row .ag-cell {
  display: flex !important;
  justify-content: center !important; /* align horizontal */
  align-items: center !important;
}
</style>
<style>
  .ag-theme-balham .ag-row .ag-cell .statustag {
    border-radius: 3px;
    padding: 0px 8px;
    white-space: nowrap;
  }
  .ag-theme-balham .ag-row .ag-cell .statustag.success {
    color: rgb(15, 81, 50);
    background: rgb(231, 250, 239);
    border: 1px solid rgb(189, 241, 211);
  }
  .ag-theme-balham .ag-row .ag-cell .statustag.inprogress {
    color: rgb(92, 74, 0);
    background: rgb(255, 243, 205);
    border: 1px solid rgb(255, 230, 156)
  }
  .ag-theme-balham .ag-row .ag-cell .statustag.failed {
    color: rgb(132, 32, 41);
    background: rgb(255, 236, 236);
    border: 1px solid rgb(255, 172, 172);
  }
  .ag-theme-balham .ag-row .ag-cell.ag-info-error {
    color: rgb(220, 53, 69);
    display: inline-flex;
    flex-shrink: 0;
    margin-top: 1px;
  }
  .ag-filter-apply-panel .ag-standard-button {
    -webkit-appearance: none;
    appearance: none;
    outline: 0;
    display: inline-block;
    box-sizing: border-box;
    margin: 0;
    padding: 7px 9px;
    border: 1px solid #c4c4c4;
    border-radius: 4px;
    background: #fff;
    color: #1f2d3d;
    font-size: 12px;
    line-height: 1;
    text-align: center;
    white-space: nowrap;
    cursor: pointer;
  }
  .ag-filter-apply-panel .ag-standard-button + .ag-standard-button {
    margin-left: 8px;
  }
  .ag-filter-apply-panel .ag-standard-button:hover,
  .ag-filter-apply-panel .ag-standard-button:focus {
    color: #20a0ff;
    border-color: #20a0ff;
  }
  .ag-filter-apply-panel .ag-standard-button:active {
    color: #1d90e6;
    border-color: #1d90e6;
    outline: 0;
  }
  /* Applying is the primary action, the way it is in the dialogs */
  .ag-filter-apply-panel [ref="applyFilterButton"] {
    background-color: #20a0ff;
    border-color: #20a0ff;
    color: #fff;
  }
  .ag-filter-apply-panel [ref="applyFilterButton"]:hover,
  .ag-filter-apply-panel [ref="applyFilterButton"]:focus {
    background: #4db3ff;
    border-color: #4db3ff;
    color: #fff;
  }
  .ag-filter-apply-panel [ref="applyFilterButton"]:active {
    background: #1d90e6;
    border-color: #1d90e6;
    color: #fff;
  }
</style>


// WEBPACK FOOTER //
// src/components/SigmaClientNew/ProscanList.vue